import { bind, classNames, Component, h, signal, tag } from "omi";
import hlStyles from "highlight.js/styles/atom-one-light.min.css?inline";

import { HighlightInteractionEvent } from "@/wc/editor/extensions/highlight/events.ts";
import { NWVariable } from "@/wc/editor/components/nw-variable";
import { addToClipboard } from "@/utils/clipboard.ts";
import { getNodesFromSlot } from "@/utils/elements.ts";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

export type NWCodeBlockProps = {
  language: string;
  inline?: boolean;
  codegroup?: string;
  /**
   * Whether the code block able to be copied on click. Used to prevent the copy to clipboard event from being fired when the code block is inline in a validation description.
   */
  copyable?: boolean;
};

@tag("nw-code-block")
export class NWCodeBlock extends Component<NWCodeBlockProps> {
  static css = [tailwind, hlStyles];
  static propTypes = {
    language: String,
    inline: Boolean,
    codegroup: String,
    copyable: Boolean,
  };

  private copyClicked = signal(false);

  /**
   * Extracts and combines the text content from code block nodes
   *
   * This method processes nodes from the code-content slot, handling both
   * direct text nodes and NWVariable components. It accounts for potential
   * paragraph wrappers that might contain the actual content.
   *
   * @returns {string} The combined text content from all nodes
   */
  @bind
  private getContent(): string {
    const nodes = getNodesFromSlot<HTMLElement | NWVariable>(this.shadowRoot, "code-content");

    if (!nodes || nodes.length === 0) {
      return "";
    }

    const hasWrapper = nodes.length === 1 && nodes[0]?.tagName === "P";
    const nodesToProcess = hasWrapper ? Array.from(nodes[0].childNodes) : nodes;

    return nodesToProcess
      .map(node => {
        if (node instanceof NWVariable) {
          return node.getVariableValue();
        }

        return node.textContent || "";
      })
      .join("");
  }

  @bind
  async copyToClipboard() {
    if (this.props.inline) {
      // We defer to the code extension to handle the copy to clipboard event as these nodes can be split due to te highlight feature
      const copyToClipboard = new CustomEvent(HighlightInteractionEvent.COPY_TO_CLIPBOARD, {
        detail: {
          group: this.props.codegroup,
        },
      });

      dispatchEvent(copyToClipboard);
      return;
    }

    this.copyClicked.value = true;
    setTimeout(() => (this.copyClicked.value = false), 2000);

    await addToClipboard(this.getContent());

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackCodeSnippetCopied(
        this.props.inline ? "inline" : "block",
        Store.project.id.value!,
        this.getContent(),
      );
    });
  }

  render({ inline = false, copyable = true }: NWCodeBlockProps) {
    const languageClass = `language-${this.props.language}`;

    if (inline) {
      return (
        <span class="w-fit h-fit">
          <code
            id="code-container"
            class={classNames(
              `${languageClass} mix-blend-multiply py-1 px-0.5 inline bg-secondary rounded-lg text-orangedark-600 transition-colors`,
              { "cursor-pointer": copyable },
            )}
            onClick={() => copyable && this.copyToClipboard()}
          >
            <slot id="code-content"></slot>
          </code>
        </span>
      );
    }

    return (
      <div className="relative group my-5">
        <div class="bg-gray-200 text-sm px-6 py-2 rounded-t-xl flex items-center justify-between">
          <div class="">{this.props.language}</div>
          <div class="">
            {this.copyClicked.value ? (
              <div class="flex items-center space-x-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4">
                  <path
                    fillRule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clipRule="evenodd"
                  />
                </svg>

                <p class="select-none">Copied!</p>
              </div>
            ) : (
              <button class="flex items-center space-x-1.5" onClick={this.copyToClipboard}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="size-4 transition-all"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                  />
                </svg>
                <p>Copy code</p>
              </button>
            )}
          </div>
        </div>
        <pre id={"block-container"} className={`${languageClass} py-4 px-6 hljs rounded-b-xl overflow-x-auto`}>
          <code id={"code-container"} className={`${languageClass}`}>
            <slot id="code-content"></slot>
          </code>
        </pre>
      </div>
    );
  }
}
